import React, { useState, useRef, useEffect } from 'react';
import './SignupLoginModal.scss';
import './Signup.scss';
import TagManager from 'react-gtm-module';
import { Trans, useTranslation } from 'react-i18next';

// Everything the signup step needs from the auth shell: the shared email state,
// the validators, and the view switches it hands control back through.
type Props = {
  email: string;
  setEmail: (email: string) => void;
  setShowSignupView: (showSignupView: boolean) => void;
  setShowLoginView: (showLoginView: boolean) => void;
  validatePassword: (password: string) => string;
  setShowEmailVerificationCodeInputView: (
    showEmailVerificationCodeInputView: boolean,
  ) => void;
  setLoginError: (error: string) => void;
  setAuthError?: (error: string) => void;
  validateEmail: (email: string) => string;
};

/**
 * Registration step of the auth surface: email, password, the live password
 * requirement line, and the submit CTA. Styled to the JAA-273 new UI; the
 * shell renders the header around it.
 */
const Signup = (props: Props) => {
  const {
    email,
    setEmail,
    setShowSignupView,
    setShowLoginView,
    validatePassword,
    setShowEmailVerificationCodeInputView,
    setLoginError,
    setAuthError,
    validateEmail,
  } = props;
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [passwordError, setPasswordError] = useState(''); // Add this line
  const [emailError, setEmailError] = useState(''); // Add this line

  // Add these references
  const signupEmailInputRef = useRef<HTMLInputElement>(null);
  const signupPasswordInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (signupPasswordInputRef.current) {
      signupPasswordInputRef.current.focus();
    }
  }, []);

  // Posts the credentials, then routes the user to email verification, to the
  // login view if the account already exists, or to a generic error. Sends
  // credentials because the /signup response sets the pending_signup cookie the
  // SMS fallback needs, and production is cross-origin.
  const handleSubmitSignup = async () => {
    setIsSigningUp(true);
    TagManager.dataLayer({
      dataLayer: {
        event: 'signup_button_clicked',
        category: 'User Interaction',
        action: 'Click',
        label: 'Sign Up Button',
      },
    });
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.toLowerCase(),
          password,
          user_agent: navigator.userAgent,
        }),
        credentials: 'include',
      });

      if (response.ok) {
        // Handle successful response
        setShowSignupView(false);
        setPassword('');
        TagManager.dataLayer({
          dataLayer: {
            event: 'signup_by_email_success',
            category: 'User Interaction',
            action: 'Success',
            label: 'Signup by Email Success',
            userId: email,
          },
        });
        setShowEmailVerificationCodeInputView(true);
      } else {
        const errorData = await response.json();
        if (errorData.error === 'user_exists') {
          setShowSignupView(false);
          setPassword('');
          setShowLoginView(true);
          setLoginError(t('signupLoginModal.signup.accountExists'));
        } else {
          // Generic error for other failures
          setLoginError(t('signupLoginModal.errors.genericSignupError'));
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsSigningUp(false);
    }
  };

  return (
    <div className="auth-signup">
      <h2 className="auth-signup__heading">
        <Trans i18nKey="signupLoginModal.signup.title" />
      </h2>
      <form
        className="auth-signup__form"
        onSubmit={(e) => {
          e.preventDefault();
          const emailValidationError = validateEmail(email);
          setEmailError(emailValidationError);
          if (!emailValidationError) {
            handleSubmitSignup();
          }
        }}
      >
        <div className="auth-signup__field">
          <input
            type="email"
            placeholder={t('signupLoginModal.emailPlaceholder')}
            className={`auth-signup__input${
              emailError ? ' auth-signup__input--invalid' : ''
            }`}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
            required
            ref={signupEmailInputRef}
          />
          {emailError && <p className="auth-signup__error">{emailError}</p>}
        </div>
        <div className="auth-signup__field">
          <input
            type="password"
            placeholder={t('signupLoginModal.passwordPlaceholder')}
            className="auth-signup__input"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setPasswordError(validatePassword(e.target.value));
            }}
            required
            ref={signupPasswordInputRef}
          />
          {passwordError && (
            <p className="auth-signup__requirement">{passwordError}</p>
          )}
        </div>
        <button
          className={`auth-signup__submit${
            isSigningUp ? ' auth-signup__submit--loading' : ''
          }`}
          disabled={isSigningUp || passwordError !== '' || emailError !== ''}
          type="submit"
        >
          {isSigningUp ? (
            <span className="loading-dots">
              <span></span>
            </span>
          ) : (
            <Trans i18nKey="signupLoginModal.signup.signupButton" />
          )}
        </button>
      </form>
    </div>
  );
};

export default Signup;
